import { DMY_CASUAL_FORMAT, SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import { nonNullable, sum } from 'lib/array/arrayUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import { makeDateTime } from 'tripPlanner/utils'

function getCarHireInsuranceItem(carHireInsurance: App.CarHireInsurance): App.Checkout.AnyItemBreakdownView {
  if (!carHireInsurance || carHireInsurance?.type === 'limited') {
    return {
      title: 'Insurance',
      itemType: 'car_hire',
      additionalInfo: [],
      additionalElements: [{
        id: 'limited_protection',
        left: {
          type: 'text',
          value: 'Limited Protection',
        },
        right: {
          type: 'price',
          value: 0,
        },
      }],
    }
  }

  return {
    title: 'Insurance',
    itemType: 'car_hire',
    additionalInfo: [],
    additionalElements: [{
      id: carHireInsurance.id,
      left: {
        type: 'text',
        value: 'Enhanced Protection',
      },
      right: {
        type: 'price',
        value: 0,
      },
    }],
  }
}

function getCarHireAddonsItems(selectedAddons: Record<string, App.CartCarHireAddon>, taxesAndFees?: number): App.Checkout.AnyItemBreakdownView {
  return {
    title: 'Add-ons',
    itemType: 'car_hire',
    additionalInfo: [],
    taxesAndFees,
    additionalElements: Object.values(selectedAddons).map(addon => {
      return {
        id: addon.description || '',
        left: {
          type: 'text',
          value: addon.description || '',
          quantity: addon.quantity || 1,
        },
        right: {
          type: 'price',
          value: addon.total * (addon.quantity || 1),
        },
      }
    }),
  }
}

export function getSelectedCarHireAddonsTotal(selectedAddons: Array<Record<string, App.CartCarHireAddon>>) {
  return sum(selectedAddons, addon => {
    return sum(Object.values(addon), addon => addon.total * (addon.quantity || 1))
  })
}

export function getCarHireSelectedRateOption(
  item: App.Checkout.CarHireItem,
): App.CarHireOffer['rateOptions'][number] {
  return item.offer.rateOptions.find((option) =>
    option.id === item.selectedRateOption.id &&
    option.idContext === item.selectedRateOption.idContext,
  ) ?? item.offer.rateOptions[0]
}

export function getCarHireItemView(
  item: App.Checkout.CarHireItem,
): App.Checkout.CarHireItemView {
  const rateOption = getCarHireSelectedRateOption(item)
  const isPayOnArrival = !rateOption.payNowAmount && !!rateOption.payOnArrivalAmount

  return {
    title: `${item.offer.vehicle.model} ${item.offer.guarenteedModel ? '' : 'or similar'}`,
    loyalty: {
      productType: getLoyaltyProductType(item.offer),
    },
    kind: 'car-hire',
    item,
    totals: {
      taxesAndFees: !isPayOnArrival ? rateOption.payOnArrivalAmount : 0,
      price: item.price,
      value: item.value,
      memberPrice: 0,
      memberValue: 0,
      surcharge: 0,
      extraGuestSurcharge: 0,
      carHirePayOnArrival: isPayOnArrival ? rateOption.payOnArrivalAmount : 0,
    },
    model: item.offer.vehicle.model,
    image: item.offer.vehicle.image,
    pickUpLocation: item.offer.pickUp,
    returnLocation: item.offer.dropOff,
    addons: item.offer.addons,
    vendor: {
      name: item.offer.vendor.name,
      image: {
        url: `https://ctimg-svg.cartrawler.com/supplier-images/${item.offer.vendor.name.toLowerCase().replaceAll(' ', '_')}.svg`,
        title: item.offer.vendor.name,
      },
    },
  }
}

function getCarHireCancellationPolicyItem(
  offerCancellationPolicy: App.CarHireCancellationPolicy,
): App.Checkout.AnyItemBreakdownView {
  return {
    cancellationPolicy: {
      maxHours: offerCancellationPolicy.maxHours,
      maxDate: offerCancellationPolicy.maxDate,
    },
    title: '',
    additionalInfo: [],
    additionalElements: [],
    itemType: 'car_hire',
  }
}

function getCarHireDiscountItem(
  carView: App.Checkout.CarHireItemView,
): App.Checkout.CarHireItemBreakdownView {
  const rateOption = getCarHireSelectedRateOption(carView.item)

  const discount = rateOption.payNowDiscountAmount ?? 0
  const discountPercentage = rateOption.payNowDiscountPercentage ?? 0

  if (discount <= 0) {
    return {
      title: '',
      itemType: 'car_hire',
      additionalInfo: [],
      additionalElements: [],
    }
  }

  return {
    title: '',
    itemType: 'car_hire',
    additionalInfo: [],
    additionalElements: [{
      id: 'discount',
      left: {
        type: 'text',
        value: `${discountPercentage}% off`,
      },
      right: {
        type: 'price',
        value: -discount,
      },
    }],
  }
}

export function getCarHireInsurancePriceBreakdown(
  carView: App.Checkout.CarHireItemView,
): App.Checkout.PriceBreakdownView {
  return {
    title: 'Insurance',
    price: carView.item.selectedInsurance?.total || 0,
    memberPrice: 0,
    additionalInfoText: [],
    items: [
      ...carView.item.selectedInsurance ? [getCarHireInsuranceItem(carView.item.selectedInsurance)] : [],
    ],
    itemsAlwaysVisible: true,
    alwaysShowItemPrice: true,
  }
}

export function getCarHireAddonPriceBreakdown(
  carView: App.Checkout.CarHireItemView,
): App.Checkout.PriceBreakdownView {
  const selectedCarHireAddonTotal = getSelectedCarHireAddonsTotal([carView.item.selectedAddons])
  return {
    title: 'Add-ons',
    price: selectedCarHireAddonTotal,
    memberPrice: 0,
    additionalInfoText: [],
    items: selectedCarHireAddonTotal ? [getCarHireAddonsItems(carView.item.selectedAddons, carView.totals.taxesAndFees)] : [],
  }
}

export function getCarHireItemPriceBreakdown(
  carView: App.Checkout.CarHireItemView,
): App.Checkout.PriceBreakdownView {
  const { offer } = carView.item
  const isSameLocation = offer.pickUp.name === offer.dropOff.name

  return {
    title: `${carView.model} ${carView.item.offer.guarenteedModel ? '' : 'or similar'}`,
    price: carView.totals.value,
    memberPrice: 0,
    description: isSameLocation ? `Pick up and drop-off at ${offer.pickUp.name}` : `Pick up at ${offer.pickUp.name} and drop-off at ${offer.dropOff.name}`,
    additionalInfoText: [
      `${makeDateTime(carView.item.pickUpDate, carView.item.pickUpTime).format(`${DMY_CASUAL_FORMAT}, ${SHORT_TIME_FORMAT_AM_PM}`)} → ${makeDateTime(carView.item.dropOffDate, carView.item.dropOffTime).format(`${DMY_CASUAL_FORMAT}, ${SHORT_TIME_FORMAT_AM_PM}`)}`,
      `${pluralizeToString('day', offer.duration)}`,
    ],
    items: nonNullable([
      getCarHireDiscountItem(carView),
      offer.cancellationPolicy ? getCarHireCancellationPolicyItem(offer.cancellationPolicy) : undefined,
    ]),
    itemsAlwaysVisible: true,
    alwaysShowItemPrice: true,
  }
}
